import { i18nNsFactory } from '@atlas-frontend/i18n';
import { FC, memo, useCallback, useMemo } from 'react';
import {
  getDatePeriodString,
  getDayjs,
  getDayjsNow,
  sortPeriod,
} from '../../common/utils';
import { ExecutionPeriodFormDataType } from '../schema';
import { renderLoadingType } from './utils';
import translations from '../translations/PeriodDetailsTable.translations.json';
import { formatPeriodicity, usePeriodPredefines } from './utils';
import { Table } from '../../common/tanstacktable';
import { CellContext, createColumnHelper } from '@tanstack/react-table';
import { DataObjectGroupType } from '../types';
import { SystemClassifierItem } from '../../systemClassifier/SystemClassifierItem';

const { addTranslations, useTranslation } = i18nNsFactory('period-edit-table');
addTranslations(translations);

interface PeriodViewTableProps {
  groupType: DataObjectGroupType;
  loadingType?: string;
  periodCount: number | null;
  periods: ExecutionPeriodFormDataType[];
}

const columnHelper = createColumnHelper<ExecutionPeriodFormDataType>();

export function PeriodViewTable({
  groupType,
  periodCount,
  loadingType,
  periods,
}: PeriodViewTableProps) {
  const { t } = useTranslation();
  const predefines = usePeriodPredefines();

  const hideOldRows = useCallback((row: ExecutionPeriodFormDataType) => {
    const validityEnd = row.validPeriod[1];
    if (!validityEnd) {
      return {};
    }

    const ignored = getDayjsNow().subtract(1, 'years').startOf('date');
    const end = getDayjs(validityEnd)?.startOf('date');
    return end && end.isSameOrBefore(ignored) ? { hidden: true } : {};
  }, []);

  const columns = useMemo(
    () => [
      columnHelper.accessor(
        (rowData) => formatPeriodicity(rowData, predefines),
        {
          id: 'monthsPredefined',
          header: t('column.periodicity.label'),
          enableColumnFilter: false,
          enableSorting: false,
        }
      ),
      columnHelper.accessor('validPeriod', {
        header: t('column.validPeriod.label'),
        enableColumnFilter: false,
        meta: { width: 200 },
        cell: ValidPeriodCell,
        sortingFn: (a, b) =>
          sortPeriod(a.original.validPeriod, b.original.validPeriod),
      }),
      columnHelper.accessor('periodCount', {
        header: t('column.periodCount.label'),
        enableColumnFilter: false,
        enableSorting: false,
        meta: { width: 200 },
        cell: (props) => (
          <PeriodCountCell
            {...props}
            loadingType={loadingType}
            periodCount={periodCount}
          />
        ),
      }),
      ...(groupType === DataObjectGroupType.O
        ? [
            columnHelper.accessor('sourceDataVersionId', {
              header: t('column.sourceDataVersionId.label'),
              enableColumnFilter: false,
              enableSorting: false,
              meta: { width: 220 },
              cell: SourceDataVersionCell,
            }),
          ]
        : []),
    ],
    [groupType, loadingType, periodCount, predefines, t]
  );

  return (
    <Table
      name="periods"
      data={periods}
      columns={columns}
      rowKey="id"
      defaultSort={[{ id: 'validPeriod', desc: false }]}
      rowHtmlAttributes={hideOldRows}
      sticky
      virtual
      scrollContainerSelector="#detail-layout-main-content"
    />
  );
}

const ValidPeriodCell: FC<CellContext<ExecutionPeriodFormDataType, any>> = memo(
  (props) =>
    getDatePeriodString(props.renderValue()?.[0], props.renderValue()?.[1])
);

const PeriodCountCell: FC<
  CellContext<ExecutionPeriodFormDataType, any> & {
    loadingType?: string;
    periodCount?: number | null;
  }
> = memo((props) =>
  renderLoadingType(
    props.loadingType,
    props.loadingType === 'FIXED' ? props.periodCount : props.renderValue()
  )
);

const SourceDataVersionCell: FC<CellContext<ExecutionPeriodFormDataType, any>> =
  memo((props) => {
    const itemId = props.renderValue();
    if (!itemId) return null;
    return <SystemClassifierItem code="SOURCE_DATA_VERSION" itemId={itemId} />;
  });
